import React, { useState, useEffect } from "react";
import { v4 as uuidv4 } from 'uuid';
import {
    Button,
    Input,
    Container,
    Box,
    SpaceBetween,
    Table,
    Modal,
    Select,
    Tabs,
    FormField,
    Checkbox,
    Multiselect
} from "@amzn/awsui-components-react";

import { getProcessForAppIdPromise } from "../Templates/Utils/AppActivityUtils"
import { loadFlexicoTemplates } from "src/utilities/FlexicoAPIs";
import { createAuditLog, getLogicoProcessesForAppId } from "../Templates/Utils/AppActivityUtilsAppsyncPlus"
import { createPreference, deletePreference } from "src/utilities/AppSyncPlusAPIs";
import { BANNER_ASSOCIATION_ID_TYPES, BANNER_DISPLAY_EVERYWHERE_ID, BANNER_PREFERENCE_TYPE, GROUP_BANNER_ASSOCIATION_ID, GROUP_BANNER_ASSOCIATION_ID_TYPE, GROUP_BANNER_PREFERENCE_TYPE } from "src/constants/AppConstants";
import { getOwnersForAdminUser } from "src/utilities/CloseAPIs";
import { getGroupBanners, getSingleBanners } from "src/Ateam-BI/Banners/BannerUtils";
import Banner from "./Banner";
import { formatTimestamp, getCurrentTime, isLongerArray } from "../utils/generalUtils";
import BannerCustomMultiInput from "./BannerCustomMultiInput";
import { BannerInfoMessage } from "./BannerInfoMessage";
import { ALL, bannerTabIds, DisplayOptions, displayOptions, microserviceOptions, bannerTypes } from "./BannerConstants";

export default (props) => {
    const [bannerMessage, setBannerMessage] = useState("")
    const [bannerLabel, setBannerLabel] = useState("")
    const [bannerType, setBannerType] = useState(null)
    const [selectedDisplay, setSelectedDisplay] = useState(null)
    const [dismissible, setDismissible] = useState(false)

    const [closePortalOwners, setClosePortalOwners] = useState([])
    const [urlPrefixes, setUrlPrefixes] = useState([])
    const [bannerApps, setBannerApps] = useState([])
    const [microservices, setMicroservices] = useState([])
    const [singleMicroservice, setSingleMicroservice] = useState(null)
    const [singleMicroserviceApp, setSingleMicroserviceApp] = useState(null)

    const [bannerAppOptions, setBannerAppOptions] = useState([])
    const [closePortalOwnerOptions, setClosePortalOwnerOptions] = useState([])
    const [currentBanners, setCurrentBanners] = useState([])
    const [bannerErrorMessage, setBannerErrorMessage] = useState("")
    const [bannerSuccessMessage, setBannerSuccessMessage] = useState("")
    const [updatingBanner, setUpdatingBanner] = useState(null)
    const [showUpdateBannerModal, setShowUpdateBannerModal] = useState(false)
    const [singleMicroserviceOptions, setSingleMicroserviceOptions] = useState([])
    const [loadingMicroserviceOptions, setLoadingMicroserviceOptions] = useState(false)
    const [previewDismissed, setPreviewDismissed] = useState(false)

    const [activeBannerTabId, setActiveBannerTabId] = useState(bannerTabIds.CurrentBanners)

    const getGroupDisplayLabel = (item) => {
        const info = item?.information
        if (info) {
            switch(info.selectedDisplay?.value) {
                case DisplayOptions.CLOSE_PORTAL:
                    return `Close Portals: ${info.closePortalOwners.map(closePortalOwner => closePortalOwner.label).join(", ")}`;
                case DisplayOptions.URL_PREFIX:
                    return `Url Prefixes: ${info.urlPrefixes.map(urlPrefix => urlPrefix.label).join(", ")}` 
                case DisplayOptions.APP:
                    return `Apps: ${info.bannerApps.map(bannerApp => bannerApp.label).join(", ")}` 
                case DisplayOptions.MICROSERVICE:
                    return `Microservices: ${info.microservices.map(microservice => microservice.label).join(", ")}` 
            }
        }    
        return ""    
    }

    const getDisplayLabel = (item) => {
        const display = item.information.selectedDisplay?.value
        if (item?.preferenceType === GROUP_BANNER_PREFERENCE_TYPE) {
            return getGroupDisplayLabel(item)
        } 
        switch(display) {
            case DisplayOptions.EVERYWHERE:
                return "Display Everywhere"
            case DisplayOptions.CLOSE_PORTAL:
                return `Close Portal: ${item.associationId === BANNER_DISPLAY_EVERYWHERE_ID ? "All Pages" : item?.information?.closePortalOwners[0]?.label}`
            case DisplayOptions.URL_PREFIX:
                return `Url Prefix: ${item?.information?.urlPrefixes[0]?.label}`
            case DisplayOptions.APP:
                return `App: ${item.associationId === BANNER_DISPLAY_EVERYWHERE_ID ? "All Pages" : item?.information?.bannerApps[0]?.label}`
            case DisplayOptions.MICROSERVICE:
                return `Microservice: ${item?.information?.microservices[0]?.label}`
            case DisplayOptions.SINGLE_MICROSERVICE:
                return `Microservice: ${item?.information?.singleMicroservice?.label} of App: ${item?.information?.singleMicroserviceApp?.label}`
        }
        return "No Label Could Be Generated"
    }

    const getUpdateBannerButton = (item) => {
        return (
            <Button
                variant="inline-icon"
                iconName="refresh"
                onClick={() => openUpdateBannerModal(item)}
            >
                Update Banner
            </Button>
        ) 
    }

    const getDeleteBannerButton = (item) => {
        return (
            <Button
                variant="inline-icon"
                iconName="delete-marker"
                onClick={() => deleteBannerAndAudit(item)}
            >
                Delete Banner
            </Button>
        ) 
    }

    const bannerTableColumnDefinitions = [
        { header: "ID", cell: item => item?.preferenceId},
        { header: "Label", cell: item => item?.information?.bannerLabel},
        { header: "Message", cell: item => item?.information?.bannerMessage},
        { header: "Type", cell: item => item?.information?.bannerType?.label},
        { header: "Display To", cell: getDisplayLabel },
        { header: "Dismissible", cell: item => item?.information?.dismissible?.toString() },
        { header: "Created By", cell: item => item?.createdBy},
        { header: "Created At", cell: item => formatTimestamp(item?.createdTimestamp)},
        { header: "Update Banner", cell: getUpdateBannerButton },
        { header: "Delete Banner", cell: getDeleteBannerButton },
    ]

    const loadBanners = async () => {
        const [banners, groupBanners] = await Promise.all([
            getSingleBanners(), getGroupBanners()
        ])
        let groupedBanners = new Set()
        groupBanners.forEach(groupBanner => {
            const a = groupBanner.information.banners.map(b => b.bannerId)
            groupedBanners = new Set([...a, ...groupedBanners])
        })
        const bannersToLoad = banners.filter(banner => !groupedBanners.has(banner.preferenceId))
        setCurrentBanners([...bannersToLoad, ...groupBanners])
    }

    const loadClosePortalOwnerOptions = async () => {
        await getOwnersForAdminUser((result) => {
            const data = result.data.getOwnersForAdminUserRDS
            const owners = JSON.parse(data)
            const ownerSelectOptions = owners.map((owner) => {
                return {label:owner?.owner_name, value:String(owner?.owner_id)}
            })
            setClosePortalOwnerOptions(ownerSelectOptions)

        }, (response) => {
            console.error(`Error loading Close Portal Owners: ${response}`)
        })
    }

    useEffect(() => {
        loadBanners()
        loadClosePortalOwnerOptions()
    }, []);

    const loadBannerApps = (allApps) => {
        setBannerAppOptions(
            allApps.map(app => {return {"label": app.app_name, "value": app.app_id}})
        )
    }

    useEffect(() => {
        if (props?.allApps && props?.allApps?.length > 0) {
            loadBannerApps(props.allApps)
        }
    }, [props.allApps]);

    const loadFriscosForApp = async(appId) => {
        const response = await getProcessForAppIdPromise(appId);
        if (response?.data?.getProcessesForAppIDV2) {
            return response.data.getProcessesForAppIDV2  
        }
        return [];
    }

    const loadLogicosForApp = async (appId) => {
        let response = await getLogicoProcessesForAppId(appId)
        if (response?.data?.getLogicoProcessesForAppID) {
            return response.data.getLogicoProcessesForAppID
        }
        return [];
    }

    const loadFlexicosForApp = async (appId) => {
        return new Promise((resolve, reject) => {
            loadFlexicoTemplates(appId, 
                function onSuccess(result) {
                    if (result?.data?.listFlexicoTemplatesRDS) {
                        resolve(result.data.listFlexicoTemplatesRDS)
                    } else {
                        reject(null)
                    }
                }, function onFailure(error) {
                    reject(error)
            })
        })
    }

    const loadAppMicroservices = async (appId) => {
        setLoadingMicroserviceOptions(true)
        
        const [friscoProcesses, logicoProcesses, flexicoProcesses] = await Promise.all([
            loadFriscosForApp(appId),
            loadLogicosForApp(appId),
            loadFlexicosForApp(appId),
        ])

        let loadedServices = []

        friscoProcesses?.forEach(process => loadedServices.push({label: `FRISCO - ${process.label}` , value: process.process_id, service: BANNER_ASSOCIATION_ID_TYPES.FRISCO}))
        flexicoProcesses?.forEach(process => loadedServices.push({label: `FLEXICO - ${process.label}` , value: process.flex_id, service: BANNER_ASSOCIATION_ID_TYPES.FLEXICO}))
        logicoProcesses?.forEach(process => loadedServices.push({label: `LOGICO - ${process.display_label}` , value: process.logico_process_id, service: BANNER_ASSOCIATION_ID_TYPES.LOGICO}))

        setSingleMicroserviceOptions(loadedServices)
        setLoadingMicroserviceOptions(false)
    }

    const canCreateBanner = () => {
        return !(
            !!bannerLabel && !!bannerMessage && !!bannerType && 
            (
                selectedDisplay?.value === DisplayOptions.EVERYWHERE
                || closePortalOwners?.length > 0
                || urlPrefixes?.length > 0
                || bannerApps?.length > 0
                || microservices?.length > 0
                || !!singleMicroservice
            )
        )
    }
    const canUpdateBanner = canCreateBanner;

    const getBannerInformation = () => {
        return {
            bannerLabel,
            bannerMessage,
            bannerType,
            bannerApps,
            closePortalOwners,
            urlPrefixes,
            selectedDisplay,
            microservices,
            singleMicroservice,
            singleMicroserviceApp,
            dismissible
        }
    }

    const shouldCreateGroupBanner = (bannerInfo) => {
        if ([DisplayOptions.CLOSE_PORTAL, DisplayOptions.APP, DisplayOptions.MICROSERVICE, DisplayOptions.URL_PREFIX].includes(bannerInfo?.selectedDisplay?.value)) {
            if (isLongerArray(bannerInfo.closePortalOwners)  || isLongerArray(bannerInfo.urlPrefixes) || isLongerArray(bannerInfo.bannerApps) || isLongerArray(bannerInfo.microservices)) {
                return true
            }
        }
        return false
    }

    const getAssociationIdAndType = (bannerInfo, index = 0) => {
        let associationId, associationIdType;
        switch(bannerInfo?.selectedDisplay?.value) {
            case DisplayOptions.EVERYWHERE:
                associationIdType = BANNER_ASSOCIATION_ID_TYPES.ABC;
                associationId = BANNER_DISPLAY_EVERYWHERE_ID;
                break;
            case DisplayOptions.CLOSE_PORTAL:
                associationIdType = BANNER_ASSOCIATION_ID_TYPES.CLOSE_PORTAL;
                associationId = bannerInfo?.closePortalOwners === ALL ? BANNER_DISPLAY_EVERYWHERE_ID : bannerInfo?.closePortalOwners[index]?.value;
                break;
            case DisplayOptions.URL_PREFIX:
                associationIdType = BANNER_ASSOCIATION_ID_TYPES.URL_PREFIX;
                associationId = bannerInfo?.urlPrefixes[index]?.label?.toLowerCase();
                break;
            case DisplayOptions.APP:
                associationIdType = BANNER_ASSOCIATION_ID_TYPES.APP;
                associationId = bannerInfo?.bannerApps === ALL ? BANNER_DISPLAY_EVERYWHERE_ID : bannerInfo?.bannerApps[index]?.value;
                break;
            case DisplayOptions.MICROSERVICE:
                associationIdType = bannerInfo.microservices[index].service;
                associationId = BANNER_DISPLAY_EVERYWHERE_ID;
                break;
            case DisplayOptions.SINGLE_MICROSERVICE:
                associationIdType = bannerInfo.singleMicroservice.service;
                associationId = bannerInfo.singleMicroservice.value;
                break;
        }
        return [associationId, associationIdType]
    }

    const createBanner = async (bannerInformation) => {
        setBannerSuccessMessage("")
        setBannerErrorMessage("")

        if (shouldCreateGroupBanner(bannerInformation)) {
            return createGroupBanner(bannerInformation);
        }

        let [associationId, associationIdType] = getAssociationIdAndType(bannerInformation);

        return await createSingleBanner(
            BANNER_PREFERENCE_TYPE,
            associationId,
            associationIdType,
            props.userId,
            bannerInformation,
        ).then((preferenceId) => {
            setBannerSuccessMessage(`Banner created successfully with preference id: ${preferenceId}`);
            resetBannerForm();
            loadBanners();
        }, (error) => {
            setBannerErrorMessage(`Something went wrong during banner creation: ${error}`)
        })
    }

    const createSingleBanner = async (
        bannerPreferenceType,
        associationId,
        associationIdType,
        userId,
        bannerInformation,
    ) => {
        const newPreferenceId = uuidv4();

        return new Promise((resolve, reject) => {
            createPreference(
                newPreferenceId,
                bannerPreferenceType,
                bannerInformation.bannerLabel,
                associationId,
                associationIdType,
                userId,
                bannerInformation,
                null, // ttl
                () => {
                    resolve(newPreferenceId)
                },
                (createError) => {
                    reject(createError)
                }
            );
        })
    }    

    const createGroupBanner = async (bannerInformation) => {
        let singleBanners;

        switch(bannerInformation.selectedDisplay?.value) {
            case DisplayOptions.CLOSE_PORTAL:
                singleBanners = bannerInformation.closePortalOwners;
                break;
            case DisplayOptions.URL_PREFIX:
                singleBanners = bannerInformation.urlPrefixes;
                break;
            case DisplayOptions.APP:
                singleBanners = bannerInformation.bannerApps;
                break;
            case DisplayOptions.MICROSERVICE:
                singleBanners = bannerInformation.microservices;
                break;
        }

        const newBanners = (await Promise.all(singleBanners.map((singleBanner, index) => {
            let [associationId, associationIdType] = getAssociationIdAndType(bannerInformation, index);
            return createSingleBanner(
                BANNER_PREFERENCE_TYPE,
                associationId,
                associationIdType,
                props.userId,
                bannerInformation,
            )
        }))).flat()
        
        const groupBannersInfo = newBanners.map((newBanner, index) => {
            return {
                bannerId: newBanner,
                ...singleBanners[index]
            }
        })

        await createSingleBanner(
            GROUP_BANNER_PREFERENCE_TYPE,
            GROUP_BANNER_ASSOCIATION_ID,
            GROUP_BANNER_ASSOCIATION_ID_TYPE,
            props.userId,
            {
                ...bannerInformation,
                banners: groupBannersInfo
            },
        ).then((preferenceId) => {
            setBannerSuccessMessage(`Group Banner created successfully with preferenceId: ${preferenceId}.`)
            resetBannerForm()
            loadBanners()
        })
    }

    const createBannerAndAudit = async (bannerInformation) => {
        await createBanner(bannerInformation)
        const isGroupBanner = shouldCreateGroupBanner(bannerInformation)
        createAuditLog(
            `${isGroupBanner ? GROUP_BANNER_PREFERENCE_TYPE : BANNER_PREFERENCE_TYPE}#`, 
            `${getCurrentTime()}`, 
            "Creating a banner", 
            JSON.stringify({}), 
            JSON.stringify(bannerInformation), 
            "CREATE BANNER", 
            props.userId,
            (onSuccess) => console.log("success!", onSuccess),
            (onFailure) => console.log("failure!", onFailure),
        )
    }

    const resetBannerForm = () => {
        setBannerMessage("")
        setBannerLabel("")
        setBannerType(null)
        setDismissible(false)

        setSelectedDisplay(null)
        setClosePortalOwners([])
        setUrlPrefixes([])
        setBannerApps([])
        setMicroservices([])
        setSingleMicroservice(null)
        setSingleMicroserviceApp(null)
    }

    const openUpdateBannerModal = (banner) => {
        setUpdatingBanner(banner)

        const bannerInfo = banner?.information
        setBannerMessage(bannerInfo?.bannerMessage)
        setBannerLabel(banner?.label)
        setBannerType(bannerInfo?.bannerType)
        setDismissible(bannerInfo?.dismissible)

        setSelectedDisplay(bannerInfo.selectedDisplay)
        setClosePortalOwners(bannerInfo?.closePortalOwners)
        setUrlPrefixes(bannerInfo?.urlPrefixes)
        setBannerApps(bannerInfo?.bannerApps)
        setMicroservices(bannerInfo?.microservices)
        setSingleMicroserviceApp(bannerInfo?.singleMicroserviceApp)
        setSingleMicroservice(bannerInfo?.singleMicroservice)

        if (bannerInfo?.selectedDisplay?.value === DisplayOptions.SINGLE_MICROSERVICE) {
            loadAppMicroservices(bannerInfo?.singleMicroserviceApp?.value)
        }
        
        setShowUpdateBannerModal(true)
    }

    const closeUpdateBannerModal = () => {
        resetBannerForm()
        setShowUpdateBannerModal(false)
    }

    const updateBanner = async () => {
        setBannerSuccessMessage("")
        setBannerErrorMessage("")

        const bannerInformation = getBannerInformation();

        await deleteBanner(updatingBanner)
        await createBanner(bannerInformation)
        closeUpdateBannerModal()

        const isGroupBanner = shouldCreateGroupBanner(updatingBanner.information)
        createAuditLog(
            `${isGroupBanner ? GROUP_BANNER_PREFERENCE_TYPE : BANNER_PREFERENCE_TYPE}#`,
            `${getCurrentTime()}`, 
            "altering a banner", 
            JSON.stringify(updatingBanner.information), 
            JSON.stringify(bannerInformation), 
            "ALTER BANNER", 
            props.userId,
            (onSuccess) => console.log("success!", onSuccess),
            (onFailure) => console.log("failure!", onFailure),
        )
    }

    const deleteBannerPromise = (id) => {
        return new Promise((resolve, reject) => {
            deletePreference(id, null, () => {
                console.log(`deleted banner with preference id: ${id}`)
                resolve(id)
            }, (deleteError) => {
                console.error("Error occured during deletion: ", deleteError)
                reject(deleteError)
            })
        })
    }

    const deleteBanner = async (item) => {
        if (item.preferenceType === GROUP_BANNER_PREFERENCE_TYPE) {
            const ids = item.information.banners.map(banner => banner.bannerId);
            const toDelete = ids.map(id => deleteBannerPromise(id));
            await Promise.all([...toDelete, deleteBannerPromise(item.preferenceId)]);
        } else {
            await deleteBannerPromise(item.preferenceId);
        }
        loadBanners();
    }

    const deleteBannerAndAudit = async (item) => {
        await deleteBanner(item);
        const isGroupBanner = shouldCreateGroupBanner(item.information)
        createAuditLog(
            `${isGroupBanner ? GROUP_BANNER_PREFERENCE_TYPE : BANNER_PREFERENCE_TYPE}#`, 
            `${getCurrentTime()}`, 
            "Deleting a banner", 
            JSON.stringify(item.information), 
            JSON.stringify({}), 
            "DELETE BANNER", 
            props.userId,
            (onSuccess) => console.log("success!", onSuccess),
            (onFailure) => console.log("failure!", onFailure),
        )
    }


    const getBannerForm = () => {
        return (
            <Box margin="m">
                <SpaceBetween size="m">
                    <FormField label="Banner Label">
                        <Input                 
                            value={bannerLabel}
                            onChange={({ detail }) => {
                                setBannerLabel(detail.value);
                            }}
                            placeholder="Type your label for the banner here..."
                        ></Input>
                    </FormField>

                    <FormField label="Banner Message">
                        <Input                 
                            value={bannerMessage}
                            onChange={({ detail }) => {
                                setBannerMessage(detail.value);
                                setPreviewDismissed(false);
                            }}
                            placeholder="Type your banner message here..."
                        ></Input>
                    </FormField>

                    <FormField label="Banner Type">
                        <Select
                            options={bannerTypes}
                            selectedOption={bannerType}
                            onChange={({ detail }) =>{
                                setBannerType(detail.selectedOption?.label === bannerType?.label ? null : detail.selectedOption)
                                setPreviewDismissed(false);
                            }}
                        />
                    </FormField>

                    <FormField label="Banner Is Dismissible">
                        <Checkbox
                            checked={dismissible}
                            onChange={() => {
                                setDismissible(!dismissible);
                            }}
                        />
                    </FormField>

                    <FormField label="Where Will The Banner Be Displayed">
                        <Select
                                options={displayOptions}
                                selectedOption={selectedDisplay}
                                onChange={({ detail }) => {
                                    setSelectedDisplay(detail.selectedOption?.label === selectedDisplay?.label ? null : detail.selectedOption)
                                }}
                            />
                    </FormField>
                    {
                        selectedDisplay?.value === DisplayOptions.CLOSE_PORTAL && (
                            <FormField label="Which Close Portal(s)">
                                <SpaceBetween size="m">
                                    <Checkbox
                                        checked={closePortalOwners === ALL}
                                        onChange={() => {
                                            setClosePortalOwners(closePortalOwners === ALL ? [] : ALL);
                                        }}
                                    >All Close Portal Pages</Checkbox>
                                    <Multiselect
                                        options={closePortalOwnerOptions}                 
                                        selectedOptions={closePortalOwners === ALL ? [] : closePortalOwners}
                                        filteringType="auto"
                                        inlineLabelText="Close Portals"
                                        onChange={({ detail }) => {
                                            setClosePortalOwners(detail.selectedOptions);
                                        }}
                                        disabled={closePortalOwners === ALL}
                                    />
                                </SpaceBetween>
                            </FormField>
                        )
                    }
                    {
                        selectedDisplay?.value === DisplayOptions.URL_PREFIX && (
                            <FormField label="Which Url Prefix(es)">
                                <BannerInfoMessage>Urls must be in form of {"/<url-you-want>"}. example: /firstpass</BannerInfoMessage>
                                <BannerCustomMultiInput
                                    updateInputs={(value)=>{ setUrlPrefixes(value)}}
                                    getInputs={()=>{return urlPrefixes}}
                                />
                            </FormField>
                        )
                    }
                    {
                        selectedDisplay?.value === DisplayOptions.APP && (
                            <FormField label="Which App(s)">
                                <SpaceBetween size="m">
                                    <Checkbox
                                        checked={bannerApps === ALL}
                                        onChange={() => {
                                            setBannerApps(bannerApps === ALL ? [] : ALL);
                                        }}
                                    >All App Pages</Checkbox>
                                    <Multiselect
                                        options={bannerAppOptions}
                                        selectedOptions={bannerApps === ALL ? [] : bannerApps}
                                        filteringType="auto"
                                        inlineLabelText="Apps"
                                        onChange={({ detail }) => {
                                            setBannerApps(detail.selectedOptions);
                                        }}
                                        disabled={bannerApps === ALL}
                                    />
                                </SpaceBetween>
                            </FormField>
                        )
                    }

                    {
                        selectedDisplay?.value === DisplayOptions.MICROSERVICE && (
                                <FormField label="Which Microservice(s)">
                                    <Multiselect
                                        options={microserviceOptions}
                                        selectedOptions={microservices}
                                        onChange={({ detail }) => {
                                            setMicroservices(detail.selectedOptions);
                                        }}
                                    />
                                </FormField>
                        )
                    }

                    {
                        selectedDisplay?.value === DisplayOptions.SINGLE_MICROSERVICE && (
                            <FormField label="Which App and it's Microservice">
                                <SpaceBetween size="s">
                                    <Select
                                        inlineLabelText="App"
                                        options={bannerAppOptions}
                                        selectedOption={singleMicroserviceApp}
                                        filteringType="auto"
                                        onChange={({ detail }) => {
                                            if (detail.selectedOption?.label === singleMicroserviceApp?.label) {
                                                setSingleMicroserviceApp(null)
                                                setSingleMicroserviceOptions([])
                                                setSingleMicroservice(null)
                                            } else {
                                                setSingleMicroserviceApp(detail.selectedOption)
                                                loadAppMicroservices(detail.selectedOption?.value)
                                            }
                                        }}
                                    />
                                    <Select
                                        inlineLabelText={loadingMicroserviceOptions ? "Loading Services" : "Service"}
                                        options={singleMicroserviceOptions}
                                        selectedOption={singleMicroservice}
                                        onChange={({ detail }) => {
                                            setSingleMicroservice(detail.selectedOption?.label === singleMicroservice?.label ? null : detail.selectedOption);
                                        }}
                                        disabled={
                                            loadingMicroserviceOptions || !singleMicroserviceApp
                                        }
                                        />
                                </SpaceBetween>
                            </FormField>
                        )
                    }
                </SpaceBetween>
            </Box>
        )
    }

    const dismissPreview = () => setPreviewDismissed(true);

    const getBannerPreview = () => {
        return (
            <Box>
                <SpaceBetween size="s">
                    <Box fontWeight="bold">Banner Preview</Box>
                    <Box>
                        {bannerMessage && bannerType ? (
                            previewDismissed ? (
                                <BannerInfoMessage>Dismissed! Change Banner Message or Banner Type to view preview again</BannerInfoMessage>
                            ) : (
                                <Banner bannerMessage={bannerMessage} bannerType={bannerType.value} bannerKey="0" options={ 
                                    { 
                                        roundedCorners:[DisplayOptions.CLOSE_PORTAL, DisplayOptions.MICROSERVICE, DisplayOptions.SINGLE_MICROSERVICE].includes(selectedDisplay?.value), 
                                        onDismiss: dismissible ?  dismissPreview : null
                                    } 
                                } />
                            )
                        ) : (
                            <BannerInfoMessage>Set Banner Message and Banner Type to preview your banner</BannerInfoMessage>
                        )
                    }</Box>
                </SpaceBetween>
            </Box>
        )
        
    }

    return (
        <Container>
            <h3>Add Banners to AC Portal</h3>    

            <Tabs
                activeTabId={activeBannerTabId}
                onChange={({ detail }) => setActiveBannerTabId(detail.activeTabId)}
                tabs={[
                    {
                        label: 'Current Banners',
                        id: bannerTabIds.CurrentBanners,
                        content: (
                            <Box>
                                <Table
                                    columnDefinitions={bannerTableColumnDefinitions}
                                    items={currentBanners}
                                    stickyHeader={true}
                                    resizableColumns={true}
                                ></Table>
                            </Box>
                        )
                    },
                    {
                        label: "Create Banner",
                        id: bannerTabIds.CreateBanner,
                        content: (
                            <Box>
                                <SpaceBetween size="m">
                                    <Box>
                                        {bannerSuccessMessage && (
                                            <Banner bannerMessage={bannerSuccessMessage} bannerType="success" bannerKey="0" options={{
                                                onDismiss: () => setBannerSuccessMessage(),
                                                roundedCorners: true,
                                            }}/>
                                        )}
                                        {bannerErrorMessage && (
                                            <Banner bannerMessage={bannerErrorMessage} bannerType="error" bannerKey="0" options={{
                                                onDismiss: () => setBannerErrorMessage(),
                                                roundedCorners: true,
                                            }}/>
                                        )}
                                    </Box>
                                    {getBannerPreview()}
                                    <Box>
                                        <Button variant="primary" disabled={canCreateBanner()} onClick={() => createBannerAndAudit(getBannerInformation())}>Create</Button>
                                    </Box>
                                </SpaceBetween>
                                {getBannerForm()}
                            </Box>
                        ), 
                    }
                ]}
            ></Tabs>

            <Modal
                onDismiss={() => closeUpdateBannerModal()}
                visible={showUpdateBannerModal}
                footer={
                    <Box float="right">
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button variant="link" onClick={() => { closeUpdateBannerModal() }}>Cancel</Button>
                            <Button disabled={canUpdateBanner()} variant="primary" onClick={() => {
                                updateBanner()
                            }}>Update</Button>
                        </SpaceBetween>
                    </Box>
                }
                header="Update Banner"
            >
                <SpaceBetween size="m">
                    {
                        getBannerPreview()
                    }
                    {
                        getBannerForm()
                    }
                </SpaceBetween>

            </Modal>
        </Container>
    )
}
